import React from 'react';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { SkeletonBlock } from './SkeletonBlock';

// Default footprint for a ServedFace shimmer — a short German word's worth of pill, the same scale
// DecisionBadges' own per-badge skeleton already uses (RESOLUTION_PILL_*, further down this file).
// Kept as its own pair rather than sharing those constants: this component's callers are free to
// override per their own real content's measured width, and a coincidence of two unrelated numbers
// is not a dependency worth taking.
const SERVED_FACE_SKELETON_WIDTH = 72;
const SERVED_FACE_SKELETON_HEIGHT = 16;

// ─── ServedFace ──────────────────────────────────────────
// <purpose>Renders a served FACE (badge-faces round) honestly, wherever a surface reads
// `useCategoryMeta().face(family, key)` to paint a word on screen — the one place the three-state
// rule (word / served null / not-yet-loaded) meets the DOM, so no consumer hand-rolls its own
// partial version of it (measured, final review: four consumers painted the raw key on a cold load
// and would have crashed on a served null, since `resolveFace`'s old return type lied about ever
// producing one).</purpose>
// <dependencies>useCategoryMeta().configLoading + .face; SkeletonBlock for the loading
// shimmer.</dependencies>
// <constraints>Three states, in order:
//   1. `configLoading` — the vocabulary has not landed yet, so `face` cannot be trusted (it would
//      answer the RAW KEY, same as an absent family). A shimmer, never the key, never a stand-in
//      word (UI Layout Stability: width/height are caller-supplied to match the real content).
//   2. a served `null` — the tenant's own explicit "do not put this on a row" (§13.4). Renders
//      nothing at all by default — not the key, not an empty pill — exactly like every other
//      served-null face this app already honours (resolveAttributeFace's sibling rule). `nullAs`
//      overrides this for the one shape that cannot honour it: a TAB CANNOT BE NAMELESS, so a tab
//      title passes `nullAs="key"` to fall back to the raw key rather than vanish (Task R2).
//   3. a served string (or the absent-family/key raw-key floor, which is a STRING, not null) —
//      `render(word)` draws whatever markup the caller needs around it (icon, testid, title);
//      `render` is never called for state 1, so a caller's markup can assume a real word.</constraints>
export const ServedFace: React.FC<{
  family: string;
  faceKey: string;
  width?: number | string;
  height?: number | string;
  render: (word: string) => React.ReactNode;
  // See constraint 2 above — default 'nothing' preserves every existing caller's behaviour.
  nullAs?: 'nothing' | 'key';
}> = ({ family, faceKey, width = SERVED_FACE_SKELETON_WIDTH, height = SERVED_FACE_SKELETON_HEIGHT, render, nullAs = 'nothing' }) => {
  const cm = useCategoryMeta();
  if (cm.configLoading) {
    return <SkeletonBlock width={width} height={height} borderRadius={10} style={{ flexShrink: 0 }} />;
  }
  const word = cm.face(family, faceKey);
  if (word === null) {
    if (nullAs === 'key') return <>{render(faceKey)}</>;
    return null;
  }
  return <>{render(word)}</>;
};
